import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../Templates/Header';
import Footer from '../Templates/Footer';
import Hero3D from './Hero3D';
import { showingService } from '../../_services/showing.service';
import { APP_NAME } from '../../brand';

// Sample events shown until the marketplace has real traffic — always
// labeled "sample" so nothing reads as fake activity.
const DEMO_EVENTS = [
  { type: 'posted', place: 'Austin, TX', fee: 45, demo: true },
  { type: 'accepted', place: 'Denver, CO', fee: 40, demo: true },
  { type: 'completed', place: 'Phoenix, AZ', demo: true },
  { type: 'posted', place: 'Nashville, TN', fee: 55, demo: true },
  { type: 'accepted', place: 'Tampa, FL', fee: 35, demo: true },
  { type: 'completed', place: 'Charlotte, NC', demo: true },
  { type: 'posted', place: 'Columbus, OH', fee: 50, demo: true }
];

function timeAgo(at) {
  if (!at) return null;
  const mins = Math.max(1, Math.round((Date.now() - new Date(at).getTime()) / 60000));
  if (mins < 60) return mins + 'm ago';
  const hours = Math.round(mins / 60);
  if (hours < 24) return hours + 'h ago';
  return Math.round(hours / 24) + 'd ago';
}

function eventText(e) {
  const place = e.place || 'a nearby market';
  if (e.type === 'accepted') return `Showing agent claimed a $${e.fee || '—'} showing in ${place}`;
  if (e.type === 'completed') return `Showing completed in ${place} — report delivered`;
  return `New showing posted in ${place}${e.fee ? ` · $${e.fee}` : ''}`;
}

function Marquee({ events }) {
  // duplicate the row so the -50% translate loops seamlessly
  const row = items => items.map((e, i) => (
    <span className={'ld-tick-item ' + e.type} key={i}>
      <span className="dot" />
      {eventText(e)}
      {e.at ? <span className="ld-ticker-time">{timeAgo(e.at)}</span> : null}
      {e.demo ? <span className="ld-demo-tag">sample</span> : null}
    </span>
  ));

  return (
    <div className="ng-container ld-ticker-wrap">
      <div className="ld-ticker">
        <span className="ld-live-badge"><span className="ld-live-dot" /> LIVE</span>
        <div className="ld-marquee" aria-live="off">
          <div className="ld-marquee-track">
            {row(events)}
            {row(events)}
          </div>
        </div>
      </div>
    </div>
  );
}

function CountUp({ value, prefix }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || started.current) return undefined;

    const run = () => {
      if (started.current) return;
      started.current = true;
      const target = value || 0;
      const dur = 1500;
      const t0 = performance.now();
      const tick = now => {
        const p = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(Math.round(target * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (!('IntersectionObserver' in window)) { run(); return undefined; }
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) { run(); obs.disconnect(); }
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);

  return <div ref={ref} className="ld-stat-num">{prefix || ''}{display.toLocaleString()}</div>;
}

function useReveal() {
  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('in'));
      return undefined;
    }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('in'); obs.unobserve(en.target); } });
    }, { threshold: 0.12 });
    document.querySelectorAll('[data-reveal]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

export default function Landing() {
  const [events, setEvents] = useState(DEMO_EVENTS);
  const [stats, setStats] = useState(null);

  useReveal();

  useEffect(() => {
    let alive = true;
    const load = () => {
      showingService.activity().then(data => {
        if (!alive || data.status !== 'success') return;
        if (data.events && data.events.length >= 3) setEvents(data.events);
        setStats(data.stats);
      }).catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const statCards = [
    { value: stats ? stats.open_now : 0, label: 'Open right now' },
    { value: stats ? stats.showings_completed : 0, label: 'Showings completed' },
    { value: stats ? stats.showing_agents : 0, label: 'Verified agents' },
    { value: stats ? stats.total_requests : 0, label: 'Requests posted' }
  ];

  return (
    <div className="ng-body">
      <Header />

      <section className="ld-hero">
        <div className="ng-container ld-hero-inner">
          <div>
            <span className="ld-eyebrow"><span className="ld-live-dot" /> Agents helping agents · live</span>
            <h1>Never miss a showing. <em>Ever again.</em></h1>
            <p className="ld-hero-sub">
              Double-booked? Out of town? Post the showing, name your fee, and a licensed
              local agent covers it — with a full report in your inbox before you're back at your desk.
            </p>
            <div className="ld-cta-row">
              <Link className="ld-btn ld-btn-primary" to="/showings/post">Get a showing covered</Link>
              <Link className="ld-btn ld-btn-ghost" to="/jobs">Earn money showing homes</Link>
            </div>
            <div className="ld-trust-row">
              <span><span className="tick">✓</span> Licensed &amp; verified agents</span>
              <span><span className="tick">✓</span> You set the fee</span>
              <span><span className="tick">✓</span> Reports in minutes</span>
            </div>
          </div>

          <Hero3D
            fallback={
              <div className="ld-cards" aria-hidden="true">
                <div className="ld-float-card">
                  <div className="ld-float-thumb ld-thumb-a" />
                  <div className="ld-float-top">
                    <span className="ld-float-addr">412 Maple Crest Dr</span>
                    <span className="ld-fee-pill">$45</span>
                  </div>
                  <div className="ld-float-meta">Tomorrow · 2:00–3:00 PM · 30 min</div>
                  <div className="ld-status-line"><span className="ld-live-dot" /> 3 agents viewing</div>
                </div>
                <div className="ld-float-card">
                  <div className="ld-float-thumb ld-thumb-b" />
                  <div className="ld-float-top">
                    <span className="ld-float-addr">88 Harbor Point Ln</span>
                    <span className="ld-fee-pill">$60</span>
                  </div>
                  <div className="ld-float-meta">Sat · 10:30 AM · 2 stops</div>
                  <div className="ld-status-line" style={{ color: '#1c6a47' }}>✓ Accepted by Maria R.</div>
                </div>
              </div>
            }
          />
        </div>
      </section>

      <Marquee events={events} />

      <div className="ng-container" data-reveal>
        <div className="ld-stats">
          {statCards.map((s, i) => (
            <div className="ld-stat" key={i}>
              <CountUp value={s.value} />
              <div className="ld-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <section className="ld-section" id="how-it-works">
        <div className="ng-container">
          <div className="ld-section-head" data-reveal>
            <span className="ld-kicker">How it works</span>
            <h2>One marketplace, <em>two sides</em> of every showing.</h2>
            <p>Post showings when you're stretched thin. Pick them up when you have time to fill. Every agent on {APP_NAME} is licensed and verified.</p>
          </div>
          <div className="ld-tracks">
            <div className="ld-track buyer" data-reveal>
              <h3>I need a showing covered</h3>
              <div className="ld-track-sub">For buyer's agents</div>
              <div className="ld-step">
                <span className="ld-step-num">1</span>
                <div><h4>Post the showing</h4><p>Address, time window, and the fee you're offering. Lockbox notes stay hidden until an agent accepts.</p></div>
              </div>
              <div className="ld-step">
                <span className="ld-step-num">2</span>
                <div><h4>A licensed agent accepts</h4><p>Verified local agents are alerted instantly. First to accept gets the job — you see their name and rating.</p></div>
              </div>
              <div className="ld-step">
                <span className="ld-step-num">3</span>
                <div><h4>Get the showing report</h4><p>Interest level, notes, and photos land in your inbox the moment the showing wraps.</p></div>
              </div>
            </div>
            <div className="ld-track shower" data-reveal>
              <h3>I want to show homes</h3>
              <div className="ld-track-sub">For showing agents</div>
              <div className="ld-step">
                <span className="ld-step-num">1</span>
                <div><h4>Verify your license</h4><p>Submit your state license once. Verification unlocks the jobs feed for your coverage area.</p></div>
              </div>
              <div className="ld-step">
                <span className="ld-step-num">2</span>
                <div><h4>Accept jobs near you</h4><p>Fee, time, and neighborhood are up front. Tap accept and the showing is yours.</p></div>
              </div>
              <div className="ld-step">
                <span className="ld-step-num">3</span>
                <div><h4>Show, report, get paid</h4><p>Run the showing, file a 60-second report, and the fee is yours.</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ld-fee-band">
        <div className="ng-container ld-fee-inner" data-reveal>
          <div>
            <h3>You set the fee. <em>The market answers.</em></h3>
            <p>No subscriptions, no gatekeeping. Busy Saturday showings command more, quick mid-week visits less — every showing names its price up front.</p>
          </div>
          <Link className="ld-btn ld-btn-primary" to="/showings/post">Post your first showing</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
